import { MSErrorResponse } from '@/addons/mailService/types/common';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { API_BASE_URL } from '@/config/constants';
import { FEATURE_LIST, FEATURES, FeatureType } from '@/config/features';
import { notification } from '@/shared/features/notification/notification';
import { msg } from '@/shared/helpers/msg';
import { IErrorResponse } from '@/shared/types/api';
import { IAccessToken } from '@/shared/types/auth';
import { authLogout } from '@/store/auth/authActions';
import { setMaintenance } from '@/store/global/globalSlice';
import { getErrorText, getNormalizedArgs } from '@/store/helpers';
import type { RootState } from '@/store/store';
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import qs from 'query-string';
import { refreshAccessToken } from './auth/authSlice';
import { RTKBaseQueryFn } from '@/shared/types/rtkq';
import { Mutex } from 'async-mutex';
// import { abortControllerManager } from '@/shared/helpers/rtk';
export const mutex = new Mutex();

const baseQuery: RTKBaseQueryFn = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  paramsSerializer: (params) => qs.stringify(params ?? {}, { skipEmptyString: true, skipNull: true }),
  // responseHandler: async (response) => {
  //   const text = await response.text();
  //
  //   if (!text) {
  //     return {};
  //   }
  //
  //   try {
  //     return JSON.parse(text);
  //   } catch (error) {
  //     console.error('[GLOBAL RESPONSE PARSE FAILED]', {
  //       url: response.url,
  //       status: response.status,
  //       contentType: response.headers.get('content-type'),
  //       textPreview: text.slice(0, 500),
  //       error,
  //     });
  //
  //     return {};
  //   }
  // },
  prepareHeaders: (headers, { getState, arg }) => {
    const { accessToken, refreshToken } = (getState() as RootState).auth.tokens;
    const isRefreshing = typeof arg == 'object' && arg.url === '/auth/refresh';

    const token = isRefreshing ? refreshToken : accessToken;
    token && headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

const baseQueryWithErrorHandlers: RTKBaseQueryFn = async (args, api, extraOptions) => {
  extraOptions?.log && console.log('baseQueryWithReauth > log', { args, api, extraOptions });
  // Create a new abort controller for this request

  const normalizedArgs = getNormalizedArgs(args);

  // const { signal } = abortControllerManager.createController();

  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  const result = await baseQuery(
    typeof normalizedArgs === 'string' ? { url: normalizedArgs, signal: api.signal } : { ...normalizedArgs, signal: api.signal },
    api,
    extraOptions,
  );

  // if request is successful do nothing
  if (!result.error) return result; // OK

  // -- ERROR HANDLING --
  const { status } = result.error;

  if (status === 503) {
    api.dispatch(setMaintenance(true));
    return result;
  }

  // Handle 401 Unauthorized & refresh token -> "refreshAccessTokenAndRetry"
  if (status === 401) {
    console.log('401 Unauthorized');
    return refreshAccessTokenAndRetry(args, api, extraOptions);
  }

  if (status === 403) {
    if (api.endpoint === 'viewCurrentUserProfile') {
      await api.dispatch(authLogout());
    }
    msg.error('Action not allowed');

    // During debug roles...
    try {
      const errorMessage = JSON.parse(JSON.stringify(result.error?.data)).error;
      errorMessage && console.error(result.meta?.request.method, result.meta?.request.url, errorMessage?.replace('`', ''));
    } catch (e) {
      console.error(result.error?.data);
    }

    return result;
  }

  const data = (result?.error as IErrorResponse & MSErrorResponse)?.data;

  if (!extraOptions?.customErrorHandler) {
    if (status === 404) return result;
    const message: string = data?.error?.replaceAll(/`/g, '').substring(0, 200) || data?.detail || 'Server error';

    const errorText = getErrorText(normalizedArgs, result?.error as IErrorResponse & MSErrorResponse);

    notification.error({
      message: (
        <>
          {typeof message === 'object' ? <pre>{JSON.stringify(message, null, 2)}</pre> : message}
          <InfoTooltip text={errorText} />
        </>
      ),
    }); // notification instead of msg -> coz: large text
  }
  console.error(
    '❗️ API',
    JSON.stringify({
      endpoint: api.endpoint,
      args: normalizedArgs,
      status: result.error?.status,
      originalStatus: (result.error as any)?.originalStatus,
      errorData: (result.error as any)?.data,
      errorMessage: (result.error as any)?.error,
      result,
    }),
  ); // always show console.error

  return result;
};

export const refreshAccessTokenAndRetry: RTKBaseQueryFn = async (args, api, extraOptions) => {
  extraOptions && console.log('[baseQueryWithReauth] log', { args, api, extraOptions });

  if (mutex.isLocked()) {
    await mutex.waitForUnlock();
    return baseQuery(args, api, extraOptions); // retry original request
  }
  const release = await mutex.acquire();

  try {
    // Request new access token
    const refreshResult = await baseQuery({ url: '/auth/refresh', method: 'POST' }, api, extraOptions);
    const { accessToken } = refreshResult.data as IAccessToken;
    api.dispatch(refreshAccessToken({ accessToken })); // replace accessToken with new
    return baseQuery(args, api, extraOptions); // retry original request
  } catch (error) {
    // Refresh token failed -> Logout
    console.log('Refresh token failed. Logout', { error, args, api });
    api.dispatch(authLogout());
    return { error: { status: 'CUSTOM_ERROR', error: 'Refresh token failed' } };
  } finally {
    // Release mutex -> allow next request
    release();
  }
};

/*
 * Define a service using app URL and expected endpoints
 * Enhance generated endpoints with tags: providesTags & invalidatesTags
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#tags
 * More at: https://www.graphql-code-generator.com/plugins/typescript-rtk-query
 */

export const emptyApi = createApi({
  reducerPath: '_api',
  tagTypes: FEATURE_LIST,
  refetchOnReconnect: true,
  refetchOnFocus: true,
  baseQuery: retry<RTKBaseQueryFn>(baseQueryWithErrorHandlers, { maxRetries: 0 }),
  endpoints: () => ({}),
});

/*
 * Same tags for all entities!
 * Works great!
 */
export const getTags = (type: FeatureType, dependentTypes?: FeatureType[]) => ({
  // Mutations
  invalidatesTags: (result, error, args) => {
    const tags: { type: FeatureType; id?: string }[] = [
      { type }, // main (feature has been updated -> mark data as outdated
    ];

    // Single view
    const id = typeof args === 'number' ? args : args?.id;
    if (id && Number(id)) tags.push({ type, id: `${id}` });

    // single view of dependentTypes
    dependentTypes?.forEach((type) => tags.push({ type }));

    return tags;
  },
  // Queries
  providesTags: (result, error, id) => [{ type, id }], // for single view only
  // Search view ->  providesTags: () => [type, { type, id: 'search' }],
});

export const FILE_DEPENDENT_TAGS: FeatureType[] = [
  'Revision',
  'Grading',
  'Ticket',
  'User',
  'Customer',
  'Writer',
  'WriterPass',
  'WriterOrder',
  'TestEssay',
  'TestQuiz',
  'TestEssayUndertaking',
  'TestQuizUndertaking',
  'PlagCheck',
  'PlagCheckItem',
  'AICheck',
  'AICheckItem',
];

export const getFeaturePath = (type: FeatureType) => {
  const { apiPath, path } = FEATURES[type];

  return apiPath || path;
};
